export function getZipFromFeature(feature) {
  const zip =
    feature?.properties?.ZCTA5CE10 ??
    feature?.properties?.zipcode ??
    feature?.properties?.ZIPCODE;
  return zip != null ? String(zip).trim() : '';
}

export function getCenterOfPolygon(geometry) {
  if (!geometry?.coordinates) return null;

  let coords = [];
  if (geometry.type === 'Polygon') {
    coords = geometry.coordinates[0];
  } else if (geometry.type === 'MultiPolygon') {
    coords = geometry.coordinates[0][0];
  } else {
    return null;
  }

  const len = coords.length;
  if (!len) return null;

  const [lngSum, latSum] = coords.reduce(
    (acc, [lng, lat]) => [acc[0] + lng, acc[1] + lat],
    [0, 0]
  );
  return [latSum / len, lngSum / len];
}

/** Prefer Census internal point; fall back to polygon centroid. */
export function getFeatureCoordinates(feature) {
  const lat = parseFloat(feature?.properties?.INTPTLAT10);
  const lng = parseFloat(feature?.properties?.INTPTLON10);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return [lat, lng];
  }
  return getCenterOfPolygon(feature?.geometry);
}

export function parseCommaSeparatedZips(input) {
  return [...new Set(
    String(input ?? '')
      .split(/[,;\s]+/)
      .map((z) => z.replace(/\D/g, '').trim())
      .filter((z) => z.length >= 5)
      .map((z) => z.slice(0, 5))
  )];
}

export function buildZipFeatureIndex(geoJson) {
  const index = new Map();
  const features = geoJson?.features ?? [];
  for (const feature of features) {
    const zip = getZipFromFeature(feature);
    if (zip && !index.has(zip)) {
      index.set(zip, feature);
    }
  }
  return index;
}

function hasGeometryCoordinates(geometry) {
  const coords = geometry?.coordinates;
  if (!coords) return false;
  if (geometry.type === 'Polygon') {
    return Array.isArray(coords[0]) && coords[0].length > 0;
  }
  if (geometry.type === 'MultiPolygon') {
    return Array.isArray(coords[0]?.[0]) && coords[0][0].length > 0;
  }
  return Array.isArray(coords) && coords.length > 0;
}

function mapApiZipItemToFeature(item) {
  const zip = String(item?.zipcode ?? '').trim();
  if (!item?.found || !hasGeometryCoordinates(item?.geometry)) {
    return null;
  }

  return {
    type: item.featureType === 'Feature' ? 'Feature' : 'Feature',
    properties: {
      ...(item.properties && typeof item.properties === 'object' ? item.properties : {}),
      zipcode: zip,
      ZCTA5CE10: zip,
    },
    geometry: item.geometry,
  };
}

/**
 * Normalizes axios response from POST /GetMapZipcodeBoundries for map rendering.
 */
export function applyMapBoundariesApiResponse(res) {
  if (res?.hasError) {
    return {
      ok: false,
      message: res.message || 'Failed to load map boundaries.',
      notFound: [],
    };
  }

  const payload = res?.data;
  if (payload?.success !== true) {
    return {
      ok: false,
      message: payload?.message || 'Could not load map boundaries.',
      notFound: [],
    };
  }

  const inner = payload?.data ?? {};
  const items = Array.isArray(inner.zipcodes) ? inner.zipcodes : [];
  const summary = inner.summary ?? {};
  const features = [];
  const results = [];

  for (const item of items) {
    const feature = mapApiZipItemToFeature(item);
    if (!feature) continue;

    const zip = getZipFromFeature(feature);
    const coords = getFeatureCoordinates(feature);
    features.push(feature);
    if (zip && coords) {
      results.push({ zip, coords, feature });
    }
  }

  const notFoundFromSummary = Array.isArray(summary.notFoundZipcodes)
    ? summary.notFoundZipcodes.map((z) => String(z).trim()).filter(Boolean)
    : [];
  const invalid = Array.isArray(summary.invalidZipcodes)
    ? summary.invalidZipcodes.map((z) => String(z).trim()).filter(Boolean)
    : [];
  const notFound = [...new Set([...notFoundFromSummary, ...invalid])];

  if (!features.length) {
    return {
      ok: false,
      message: payload?.message || 'No ZIP boundaries found.',
      notFound,
      resolvedBy: inner.resolvedBy,
      regionalBranch: inner.regionalBranch,
    };
  }

  return {
    ok: true,
    geoJson: { type: 'FeatureCollection', features },
    results,
    notFound,
    resolvedBy: inner.resolvedBy,
    regionalBranch: inner.regionalBranch,
    servingZipcodeCount: inner.servingZipcodeCount,
    cached: payload.cached,
    message: payload.message,
  };
}
